import { useMemo, useCallback } from 'react';
import { clamp } from 'lodash';
import { MarksByPercentage } from '../types';
import { parseLeverageFromLabel } from '../utils';

interface UseSliderConvertValueParams {
	marks: MarksByPercentage;
}

interface SliderConvertValueParams {
	value: number;
	from: 'leverage' | 'percent';
}

/**
 * Hook that converts leverage <-> slider percent via linear interpolation.
 * Provides accurate conversion between leverage values and slider percentage positions.
 */
export default function useSliderConvertValue({
	marks,
}: UseSliderConvertValueParams) {
	// Create sorted scale of { percent, leverage } for convenience.
	const scale = useMemo(() => {
		return Object.entries(marks)
			.map(([percent, mark]) => ({
				percent: Number(percent),
				leverage: parseLeverageFromLabel(mark.label as string),
			}))
			.sort((a, b) => a.percent - b.percent);
	}, [marks]);

	return useCallback(
		({ value: valueRaw, from }: SliderConvertValueParams) => {
			if (scale.length === 0) {
				return 0;
			}

			const to = from === 'leverage' ? 'percent' : 'leverage';

			// Clamp value to min and max of scale.
			const min = scale[0][from];
			const max = scale[scale.length - 1][from];
			const value = clamp(valueRaw, min, max);

			// Find segment [i, i+1] such that mark_i <= value <= mark_{i+1}.
			let i = 0;
			while (i < scale.length - 1 && value > scale[i + 1][from]) {
				i++;
			}
			const leftMark = scale[i];
			const rightMark = scale[i + 1] ?? leftMark;

			// If value is exactly at a mark, just return the mark value.
			if (rightMark[from] === leftMark[from]) {
				return leftMark[to];
			}

			// Otherwise, interpolate between the two marks.
			const segmentProgress =
				(value - leftMark[from]) / (rightMark[from] - leftMark[from]);
			return leftMark[to] + segmentProgress * (rightMark[to] - leftMark[to]);
		},
		[scale]
	);
}
